import { Form } from '@webinex/antik';
import { ROLE_VALUES_FOR_EMPLOYEES, Role, OptionInitialValue } from '@app/main/api';
import { useField, useFormikContext } from 'formik';
import { EmployeeDetailsFormValue } from './EmployeeDetailsForm';
import {
  PROVIDER_INFO_INITIAL_VALUE,
  ProviderInfoFieldSet,
  ProviderInfoFieldSetValue,
} from './ProviderInfoFieldSet';
import React, { useEffect } from 'react';
import { isObject } from 'lodash';
import { useCatalogCareHomeOptionSource } from '@app/medical-record/widgets/CatalogCareHomes.Common';
import { useRoleBasedAccess } from '@app/main/utils';

export type EmployeeEmailFieldConfig =
  | 'readonly'
  | true
  | {
      showOnChange: React.ReactNode;
    };

interface Props {
  initialValue: EmployeeDetailsFormValue;
  email: EmployeeEmailFieldConfig;
  roles: 'readonly' | true;
  specialty?: OptionInitialValue | null;
}

export function EmployeeDetailsFieldSet(props: Props) {
  const {
    email,
    roles,
    initialValue: { email: initialEmail },
  } = props;
  const roleAccessMap = useRoleBasedAccess();
  const { values } = useFormikContext<EmployeeDetailsFormValue>();

  const [{ value: providerInfo }, , { setValue: setProviderInfo }] =
    useField<ProviderInfoFieldSetValue | null>('providerInfo');

  const isPerformer = values.roles.includes('PERFORMER');
  const isCareHomeStaff = values.roles.includes('CARE_HOME_STAFF');

  const careHomeOptionSource = useCatalogCareHomeOptionSource();

  useEffect(() => {
    setProviderInfo(isPerformer ? providerInfo ?? PROVIDER_INFO_INITIAL_VALUE : null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPerformer]);

  return (
    <>
      {email === 'readonly' ? (
        <Form.Item name="email">
          <Form.Text />
        </Form.Item>
      ) : (
        <>
          <Form.Item name="email" required>
            <Form.AutoFocus />
            <Form.Input autoFocus />
          </Form.Item>
          {isObject(email) && initialEmail !== values.email && email.showOnChange}
        </>
      )}
      <Form.Item name="firstName" required>
        <Form.Input />
      </Form.Item>
      <Form.Item name="lastName" required>
        <Form.Input />
      </Form.Item>
      <Form.Item name="phone">
        <Form.Phone />
      </Form.Item>
      {roles === 'readonly' ? (
        <Form.Item name="roles">
          <Form.Enum type={Role} />
        </Form.Item>
      ) : (
        <Form.Item name="roles" required>
          <Form.EnumSelect mode="multiple" type={Role} values={ROLE_VALUES_FOR_EMPLOYEES} />
        </Form.Item>
      )}
      {isPerformer && <ProviderInfoFieldSet name="providerInfo" initialSpecialty={providerInfo?.specialty} />}
      {isCareHomeStaff && (
        <Form.Item name="careHomeId">
          <Form.AsyncSelect
            optionSource={careHomeOptionSource}
            disabled={!roleAccessMap.EmployeeManagementRoles}
            preload
            allowClear
            always={undefined}
          />
        </Form.Item>
      )}
    </>
  );
}
